import axios, { InternalAxiosRequestConfig } from "axios"
import { getAuthUserToken } from "infra/security/auth-user-token"

const BASE_URL = process.env.REACT_APP_BASE_URL

/**
 * How long to wait for the server before calling a request failed.
 *
 * Axios defaults to 0, which means WAIT FOR EVER. That is what stranded a child mid-test on
 * 2026-10-27: her submit never reached the server, the promise never settled, and the player -
 * which disables every control while a request is in flight - left her with a dead Submit, a
 * dead "Skip for now" and no error to read. A request that cannot complete has to fail so the
 * page can say so and let her try again.
 *
 * 30s is far longer than anything here needs; the heaviest call is an audio clip at ~250kB.
 */
const REQUEST_TIMEOUT_MS = 30_000

export const apiClient = axios.create({ timeout: REQUEST_TIMEOUT_MS })

const bearerTokenRequestInterceptor = (config: InternalAxiosRequestConfig) => {
  const token = getAuthUserToken()

  const url = config.url

  if (BASE_URL && url && url.startsWith(BASE_URL)) {
    config.headers["Authorization"] = `Bearer ${token}`
  }
  return config
}

apiClient.interceptors.request.use(bearerTokenRequestInterceptor)


const tokenExpiredResponseProcessor = (error: any) => {

  let handled = false
  if (
    (error?.response?.status === 403 || error?.response?.status === 401) &&
    (error?.response?.data?.error === "jwt expired" || error?.response?.data?.error === "invalid algorithm" || error?.response?.data?.error === "invalid token")
  ) {
    localStorage.clear()
    window.location.href = "/login"
    handled = true
  }
  return handled
}

/**
 * ALWAYS rejects, including when the expired-token branch above has already acted on the error.
 *
 * Returning nothing from a response-error interceptor does not swallow the error - it RESOLVES
 * the chain with `undefined`, so every caller doing `response.data` threw "Cannot read
 * properties of undefined" instead of seeing the auth failure it actually was. Redirecting to
 * /login is a side effect of the failure, not a substitute for reporting it.
 */
const errorHandler = (error: any) => {
  tokenExpiredResponseProcessor(error)
  return Promise.reject(error)
}

apiClient.interceptors.response.use(
  (response) => {
    return response
  }, errorHandler)



